import { PUSH_ICON_IMAGE_EXPECTED_RATIO } from "@lib/dashboardConfig/constants";
import type { AspectRatio, SupportedImageExtensions } from "@lib/mediaLibrary";

export type PushImagePlatform = "ios" | "android" | "web" | "kindle";

export interface PushImageVariantRules {
  acceptedFileTypes: SupportedImageExtensions[];
  uploaderFileTypes: SupportedImageExtensions[];
  detectedFileType?: string;
  effectiveFileType?: string;
  expectedRatio?: AspectRatio;
  fileTypeIsInvalid: boolean;
  requiresFileType: boolean;
  variantsUnsupported: boolean;
  acceptsFileType: (fileType: string | undefined) => boolean;
  detectFileTypeFromUrl: (url: string) => string | undefined;
}

interface GetPushImageVariantRulesOptions {
  platforms: readonly PushImagePlatform[];
  url: string;
  storedFileType?: string;
}

const knownImageFileTypes = new Set([
  "apng",
  "avif",
  "bmp",
  "gif",
  "heic",
  "heif",
  "ico",
  "jpe",
  "jpeg",
  "jfif",
  "png",
  "svg",
  "svgz",
  "tif",
  "tiff",
  "webp",
]);

const iosAcceptedFileTypes: SupportedImageExtensions[] = ["jpeg", "jpg", "png", "gif"];
const squareAcceptedFileTypes: SupportedImageExtensions[] = ["jpeg", "jpg", "png"];

const normalizeFileType = (fileType: string | undefined): string | undefined => {
  const normalizedFileType = fileType?.toLowerCase();
  return normalizedFileType === "jpg" ? "jpeg" : normalizedFileType;
};

/** Parses a file extension from a URL pathname; shared with CO image eligibility checks. */
export const detectFileExtensionFromUrl = (url: string): string | undefined => {
  try {
    const pathname = new URL(url, "https://image.invalid").pathname;
    return normalizeFileType(pathname.match(/\.([^./]+)$/)?.[1]);
  } catch {
    return undefined;
  }
};

const detectFileTypeFromUrl = (url: string): string | undefined => {
  const fileType = detectFileExtensionFromUrl(url);
  return fileType && knownImageFileTypes.has(fileType) ? fileType : undefined;
};

/** Gets all image rules for the selected Push platforms and current variant value. */
export const getPushImageVariantRules = ({
  platforms,
  url,
  storedFileType,
}: GetPushImageVariantRulesOptions): PushImageVariantRules => {
  const hasSupportedPlatform = platforms.some((platform) => platform !== "kindle");
  const requiresSquareImage = platforms.some((platform) => platform === "android" || platform === "web");
  const acceptedFileTypes = hasSupportedPlatform
    ? requiresSquareImage
      ? squareAcceptedFileTypes
      : iosAcceptedFileTypes
    : [];
  const acceptsFileType = (fileType: string | undefined): boolean => {
    const normalizedFileType = normalizeFileType(fileType);
    return (
      !!normalizedFileType &&
      acceptedFileTypes.some((acceptedFileType) => normalizeFileType(acceptedFileType) === normalizedFileType)
    );
  };
  const detectedFileType = detectFileTypeFromUrl(url);
  const effectiveFileType = detectedFileType || storedFileType;

  return {
    acceptedFileTypes,
    uploaderFileTypes: Array.from(
      new Set(acceptedFileTypes.map((fileType) => (fileType === "jpg" ? "jpeg" : fileType)))
    ),
    detectedFileType,
    effectiveFileType,
    expectedRatio: requiresSquareImage ? PUSH_ICON_IMAGE_EXPECTED_RATIO : undefined,
    fileTypeIsInvalid: Boolean(effectiveFileType && !acceptsFileType(effectiveFileType)),
    requiresFileType: platforms.includes("ios"),
    variantsUnsupported: acceptedFileTypes.length === 0,
    acceptsFileType,
    detectFileTypeFromUrl,
  };
};
